// src/pages/JoinGroup.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import './JoinGroup.css';
import API_URL from '../config';

function JoinGroup() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  // Fetch group information
  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const response = await fetch(`${API_URL}/api/groups/${groupId}`);
        if (response.ok) {
          const data = await response.json();
          setGroupData(data);
        } else {
          setError(t('joinGroup.errors.groupNotFound'));
        }
      } catch (err) {
        console.error('Error fetching group:', err);
        setError(t('joinGroup.errors.somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [groupId, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      setError(t('joinGroup.errors.fillAllFields'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email
        })
      });

      if (response.ok) {
        setSuccess(true);
        setError('');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        const errorData = await response.json();
        
        // Check for specific error messages and translate them
        if (errorData.message?.includes('redan medlem') || errorData.message?.includes('already')) {
          setError(t('joinGroup.errors.alreadyMember'));
        } else {
          setError(t('joinGroup.errors.couldNotAdd'));
        }
      }
    } catch (err) {
      console.error('Error joining group:', err);
      setError(t('joinGroup.errors.tryAgain'));
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="join-group-page">
          <div className="loading">{t('joinGroup.loadingGroupInfo')}</div>
        </div>
      </div>
    );
  }

  if (error && !groupData) {
    return (
      <div>
        <Navbar />
        <div className="join-group-page">
          <div className="join-group-container error-container">
            <h1>❌ {error}</h1>
            <button onClick={() => navigate('/')} className="btn-home">
              {t('joinGroup.goToHomepage')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div>
        <Navbar />
        <div className="join-group-page">
          <div className="join-group-container success-container">
            <div className="success-icon">✓</div>
            <h1>{t('joinGroup.success.title')}</h1>
            <p>{t('joinGroup.success.message', { groupName: groupData.groupName })}</p>
            <p>{t('joinGroup.success.notifications')}</p>
            <div className="redirect-info">
              {t('joinGroup.success.redirecting')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="join-group-page">
        <div className="join-group-container">
          <div className="group-info-card">
            <h1>{t('joinGroup.title')}</h1>
            <div className="group-details">
              <h2>{groupData.groupName}</h2>
              <div className="detail-row">
                <span className="detail-label">{t('joinGroup.when')}</span>
                <span className="detail-value">
                  {groupData.meetingFrequency.dayOfWeek} {t('joinGroup.atTime')} {groupData.meetingFrequency.time}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('joinGroup.location')}</span>
                <span className="detail-value">{groupData.location}</span>
              </div>
            </div>
          </div>

          {/* Google Ads Placeholder */}
          <div className="ad-container">
            <div className="ad-placeholder">
              {/* 
                REPLACE THIS DIV WITH YOUR GOOGLE ADSENSE CODE
                Example:
                <ins className="adsbygoogle"
                     style={{display:'block'}}
                     data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
                     data-ad-slot="1234567890"
                     data-ad-format="auto"
                     data-full-width-responsive="true"></ins>
              */}
              <span className="ad-label">{t('joinGroup.ad')}</span>
              <p>{t('joinGroup.adPlaceholder')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="join-form">
            <h3>{t('joinGroup.formTitle')}</h3>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label>{t('joinGroup.yourName')} *</label>
              <input
                type="text"
                placeholder={t('joinGroup.namePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('joinGroup.yourEmail')} *</label>
              <input
                type="email"
                placeholder={t('joinGroup.emailPlaceholder')}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
              <small>{t('joinGroup.emailInfo')}</small>
            </div>

            <button type="submit" className="btn-join">
              {t('joinGroup.joinButton')} 🎉
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default JoinGroup;